import { useState, useEffect } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { PRESET_BADGES, BadgeIcon } from "@/components/Badges";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Copy, Check, Ticket, Users, Trash2, ExternalLink, Loader2, Shield, Coins, Minus } from "lucide-react";

export default function Admin() {
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    try {
      const [c, u] = await Promise.all([api.get("/admin/invite-codes"), api.get("/admin/users")]);
      setCodes(c.data);
      setUsers(u.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/invite-codes");
      toast.success(`Invite code ${data.code} generated`);
      load();
    } catch {
      toast.error("Could not generate code");
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success("Invite code copied to clipboard");
      setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt("Copy this invite code:", code);
    }
  };

  const removeUser = async (id, username) => {
    if (!window.confirm(`Delete user @${username}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(`Deleted @${username}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete user");
    }
  };

  const activeCodes = codes.filter((c) => !c.used);
  const usedCodes = codes.filter((c) => c.used);
  const unused = activeCodes.length;

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="rounded-2xl border border-[#22222B] bg-[#111115] p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-violet-600/15 border border-violet-500/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-violet-400" />
      </div>
      <div>
        <p className="text-2xl font-serif font-bold text-white">{value}</p>
        <p className="text-xs text-zinc-500">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-28 pb-16 px-4 max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-5 h-5 text-rose-400" />
          <h1 className="font-serif font-bold text-3xl text-white">Admin Panel</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-6">Generate invite codes and manage the community.</p>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <Stat icon={Users} label="Total users" value={users.length} />
          <Stat icon={Ticket} label="Unused codes" value={unused} />
          <Stat icon={Ticket} label="Total codes" value={codes.length} />
        </div>

        <Tabs defaultValue="active">
          <TabsList className="bg-[#111115] border border-[#22222B] rounded-xl p-1 flex-wrap h-auto">
            <TabsTrigger value="active" data-testid="admin-tab-active" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Ticket className="w-4 h-4 mr-1.5" />Active Codes</TabsTrigger>
            <TabsTrigger value="used" data-testid="admin-tab-used" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Check className="w-4 h-4 mr-1.5" />Used Codes</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Users className="w-4 h-4 mr-1.5" />Users</TabsTrigger>
          </TabsList>

          {/* ACTIVE CODES */}
          <TabsContent value="active" className="mt-5">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-zinc-500">{activeCodes.length} unused code{activeCodes.length === 1 ? "" : "s"} ready to share.</p>
              <Button onClick={generate} disabled={generating} data-testid="generate-code-btn" className="bg-violet-600 hover:bg-violet-500 text-white rounded-full px-5 shadow-[0_0_20px_rgba(139,92,246,0.4)]">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Generate code
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-500 animate-spin" /></div>
            ) : (
              <div className="rounded-2xl border border-[#22222B] bg-[#111115] divide-y divide-[#1c1c24]" data-testid="active-codes-list">
                {activeCodes.length === 0 && <p className="p-6 text-sm text-zinc-600 text-center">No active codes. Generate one to invite someone.</p>}
                {activeCodes.map((c) => (
                  <div key={c.code} className="flex items-center gap-3 p-4" data-testid={`code-${c.code}`}>
                    <span className="font-mono text-base tracking-widest text-white">{c.code}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">active</span>
                    <div className="flex-1" />
                    <button onClick={() => copy(c.code)} data-testid={`copy-${c.code}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors">
                      {copied === c.code ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />} Copy
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* USED CODES */}
          <TabsContent value="used" className="mt-5">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-500 animate-spin" /></div>
            ) : (
              <div className="rounded-2xl border border-[#22222B] bg-[#111115] divide-y divide-[#1c1c24]" data-testid="used-codes-list">
                {usedCodes.length === 0 && <p className="p-6 text-sm text-zinc-600 text-center">No codes have been used yet.</p>}
                {usedCodes.map((c) => (
                  <div key={c.code} className="flex items-center gap-3 p-4" data-testid={`code-${c.code}`}>
                    <span className="font-mono text-base tracking-widest text-zinc-400 line-through">{c.code}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-400">used by @{c.used_by}</span>
                    <div className="flex-1" />
                    {c.used_at && <span className="text-xs text-zinc-600">{new Date(c.used_at).toLocaleDateString()}</span>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-5">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-500 animate-spin" /></div>
            ) : (
              <div className="rounded-2xl border border-[#22222B] bg-[#111115] divide-y divide-[#1c1c24]" data-testid="users-list">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`user-${u.username}`}>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center font-serif font-bold text-white text-sm">
                      {u.username[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium flex items-center gap-2">
                        @{u.username}
                        {u.role === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-400/10 text-rose-400 border border-rose-400/20">ADMIN</span>}
                      </p>
                      <p className="text-xs text-zinc-500">{u.views} views · <span className="text-violet-300" data-testid={`tokens-${u.username}`}>{u.custom_badge_tokens || 0} badge tokens</span></p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1 ml-2">
                      {(u.profile?.badges || []).map((b) => <BadgeIcon key={b} name={b} size={13} />)}
                    </div>
                    <div className="flex-1" />
                    <a href={`/${u.username}`} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-white transition-colors" title="View profile">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <TokenManager user={u} onSaved={load} />
                    <BadgeManager user={u} onSaved={load} />
                    {u.role !== "admin" && (
                      <button onClick={() => removeUser(u.id, u.username)} data-testid={`delete-user-${u.username}`} className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function TokenManager({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(user.custom_badge_tokens || 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/tokens`, { tokens: Number(count) || 0 });
      toast.success(`@${user.username} now has ${Number(count) || 0} custom badge token(s)`);
      setOpen(false);
      onSaved();
    } catch {
      toast.error("Could not update tokens");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setCount(user.custom_badge_tokens || 0); }}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-[#22222B] text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid={`manage-tokens-${user.username}`}>
          <Coins className="w-3.5 h-3.5" /> Tokens
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#111115] border-[#22222B] text-white sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif">Custom Badge Tokens</DialogTitle>
          <DialogDescription className="text-zinc-500">Grant @{user.username} tokens — each lets them create one custom badge.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center gap-4 py-4">
          <button onClick={() => setCount((c) => Math.max(0, Number(c) - 1))} data-testid="token-minus" className="w-10 h-10 rounded-xl bg-[#18181F] border border-[#22222B] flex items-center justify-center text-white hover:border-violet-500/40">
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            min={0}
            value={count}
            data-testid="token-input"
            onChange={(e) => setCount(e.target.value)}
            className="w-20 text-center bg-[#18181F] border border-[#22222B] rounded-xl py-2.5 text-2xl font-bold text-white outline-none focus:border-violet-500/60"
          />
          <button onClick={() => setCount((c) => Number(c) + 1)} data-testid="token-plus" className="w-10 h-10 rounded-xl bg-[#18181F] border border-[#22222B] flex items-center justify-center text-white hover:border-violet-500/40">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-tokens-btn" className="bg-violet-600 hover:bg-violet-500 text-white rounded-full">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save tokens
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function BadgeManager({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(user.profile?.badges || []);
  const [saving, setSaving] = useState(false);

  const toggle = (b) => setSelected((s) => (s.includes(b) ? s.filter((x) => x !== b) : [...s, b]));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/badges`, { badges: selected });
      toast.success(`Badges updated for @${user.username}`);
      setOpen(false);
      onSaved();
    } catch {
      toast.error("Could not update badges");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-xs px-2.5 py-1 rounded-lg border border-[#22222B] text-zinc-400 hover:border-violet-500/40 hover:text-violet-300 transition-colors" data-testid={`manage-badges-${user.username}`}>
          Badges
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#111115] border-[#22222B] text-white">
        <DialogHeader>
          <DialogTitle className="font-serif">Badges for @{user.username}</DialogTitle>
          <DialogDescription className="text-zinc-500">Select the badges to display on this user's public profile.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 py-2">
          {PRESET_BADGES.map((b) => {
            const active = selected.includes(b);
            return (
              <button key={b} onClick={() => toggle(b)} data-testid={`admin-badge-${b.toLowerCase()}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-violet-600 border-violet-500 text-white" : "bg-[#18181F] border-[#22222B] text-zinc-400"}`}>
                {active && <Check className="w-3 h-3" />} {b}
              </button>
            );
          })}
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-badges-btn" className="bg-violet-600 hover:bg-violet-500 text-white rounded-full">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save badges
        </Button>
      </DialogContent>
    </Dialog>
  );
}
